import { useState } from 'react';

const HtmlEmailEditor = ({ 
  value = '', 
  onChange, 
  placeholder = 'Enter your email message...', 
  disabled = false,
  rows = 8,
  className = '',
  showPreview = true 
}) => {
  const [activeTab, setActiveTab] = useState('editor');

  const handleChange = (e) => {
    if (onChange) {
      onChange(e.target.value);
    }
  };

  const insertHtmlTag = (tag, hasClosing = true) => {
    const textarea = document.getElementById('html-email-textarea');
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = value.substring(start, end);
    
    let newText;
    if (hasClosing) {
      newText = value.substring(0, start) + 
                `<${tag}>${selectedText}</${tag}>` + 
                value.substring(end);
    } else {
      newText = value.substring(0, start) + 
                `<${tag}>` + 
                value.substring(end);
    }
    
    if (onChange) {
      onChange(newText);
    }
    
    // Set focus back to textarea
    setTimeout(() => {
      textarea.focus();
      const newPosition = start + tag.length + 2 + (hasClosing ? 0 : 0);
      textarea.setSelectionRange(newPosition, newPosition);
    }, 0);
  };

  const insertLineBreak = () => {
    const textarea = document.getElementById('html-email-textarea');
    const start = textarea.selectionStart;
    const newText = value.substring(0, start) + '<br>\n' + value.substring(start);
    
    if (onChange) {
      onChange(newText);
    }
    
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(start + 5, start + 5);
    }, 0);
  };

  const formatTemplate = () => {
    const template = `<div style="font-family: Arial, sans-serif; line-height: 1.6; color: #333; max-width: 600px;">
  <p>Dear [Client Name],</p>
  
  <p>Your message content goes here...</p>
  
  <p><strong>Important details:</strong></p>
  <ul>
    <li>Point 1</li>
    <li>Point 2</li>
  </ul>
  
  <p>Thank you for your business!</p>
  
  <p>Best regards,<br>
  <strong>[Your Company Name]</strong></p>
</div>`;
    
    if (onChange) {
      onChange(template);
    }
  };

  return (
    <div className={`html-email-editor ${className}`}>
      {/* Toolbar */}
      <div className="border border-gray-300 rounded-t-md bg-gray-50 p-3">
        <div className="flex flex-wrap gap-1 mb-3">
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => insertHtmlTag('strong')}
            disabled={disabled}
            title="Bold"
          >
            <i className="fas fa-bold text-xs"></i>
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => insertHtmlTag('em')}
            disabled={disabled}
            title="Italic"
          >
            <i className="fas fa-italic text-xs"></i>
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => insertHtmlTag('u')}
            disabled={disabled}
            title="Underline"
          >
            <i className="fas fa-underline text-xs"></i>
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1"></div>
          
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium"
            onClick={() => insertHtmlTag('p')}
            disabled={disabled}
            title="Paragraph"
          >
            P
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={insertLineBreak}
            disabled={disabled}
            title="Line Break"
          >
            <i className="fas fa-level-down-alt text-xs"></i>
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1"></div>
          
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => insertHtmlTag('ul')}
            disabled={disabled}
            title="Unordered List"
          >
            <i className="fas fa-list-ul text-xs"></i>
          </button>
          <button
            type="button"
            className="inline-flex items-center justify-center w-8 h-8 text-gray-600 bg-white border border-gray-300 rounded hover:bg-gray-100 hover:text-gray-800 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => insertHtmlTag('li')}
            disabled={disabled}
            title="List Item"
          >
            <i className="fas fa-list text-xs"></i>
          </button>
          
          <div className="w-px h-6 bg-gray-300 mx-1"></div>
          
          <button
            type="button"
            className="inline-flex items-center px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 border border-blue-300 rounded hover:bg-blue-100 hover:text-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={formatTemplate}
            disabled={disabled}
            title="Insert Template"
          >
            <i className="fas fa-file-code mr-1"></i>
            Template
          </button>
        </div>
        
        {showPreview && (
          <div className="inline-flex rounded-md shadow-sm" role="group">
            <button
              type="button"
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium border rounded-l-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                activeTab === 'editor'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
              }`}
              onClick={() => setActiveTab('editor')}
            >
              <i className="fas fa-code mr-1"></i>
              HTML Editor
            </button>
            <button
              type="button"
              className={`inline-flex items-center px-3 py-1.5 text-sm font-medium border border-l-0 rounded-r-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${
                activeTab === 'preview'
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-blue-600 border-blue-300 hover:bg-blue-50'
              }`}
              onClick={() => setActiveTab('preview')}
            >
              <i className="fas fa-eye mr-1"></i>
              Preview
            </button>
          </div>
        )}
      </div>

      {/* Content Area */}
      <div className="border border-t-0 border-gray-300 rounded-b-md">
        {activeTab === 'editor' || !showPreview ? (
          <textarea
            id="html-email-textarea"
            className="block w-full px-3 py-2 border-0 rounded-b-md resize-y focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed"
            value={value}
            onChange={handleChange}
            placeholder={placeholder}
            rows={rows}
            disabled={disabled}
            style={{ 
              fontFamily: 'Monaco, Menlo, "Ubuntu Mono", monospace',
              fontSize: '13px'
            }}
          />
        ) : (
          <div className="p-4 bg-white rounded-b-md" style={{ minHeight: `${rows * 1.5}em` }}>
            <div className="border border-gray-200 rounded p-3 bg-gray-50 mb-3">
              <div className="text-sm text-gray-600">
                <i className="fas fa-info-circle mr-1"></i>
                Email Preview (how it will appear to recipients)
              </div>
            </div>
            <div 
              dangerouslySetInnerHTML={{ __html: value || '<p class="text-gray-500">No content to preview</p>' }}
              className="min-h-24 border border-dashed border-gray-300 p-4 rounded"
            />
          </div>
        )}
      </div>

      {/* Helper Text */}
      <div className="mt-2 text-sm text-gray-600">
        <i className="fas fa-lightbulb mr-1"></i>
        <strong>Tips:</strong> Use the toolbar buttons or type HTML directly. 
        Common tags: <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">&lt;p&gt;</code> for paragraphs, <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">&lt;br&gt;</code> for line breaks, 
        <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">&lt;strong&gt;</code> for bold, <code className="px-1 py-0.5 bg-gray-100 rounded text-xs">&lt;em&gt;</code> for italic.
        {showPreview && (
          <span> Switch to Preview tab to see how your email will look.</span>
        )}
      </div>
    </div>
  );
};

export default HtmlEmailEditor;